import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useProject } from '@/contexts/ProjectContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft, RefreshCw, Loader2, Plus, X, GitCompare, CalendarDays,
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, FileText, Trash2, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';
import ModuleInsightsPanel from '@/components/common/ModuleInsightsPanel';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Cell, ReferenceLine
} from 'recharts';
import { AgGridReact } from 'ag-grid-react';

function fmt(val) {
  if (val === undefined || val === null || val === 0) return '0';
  if (Math.abs(val) >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (Math.abs(val) >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

const REASON_COLORS = {
  INITIAL: '#3B82F6', SCOPE_CHANGE: '#143A6B', COST_ESCALATION: '#F97316',
  MANAGEMENT_RESERVE: '#0EA5E9', RE_BASELINE: '#22C55E'
};

const CAT_COLORS = ['#3B82F6', '#143A6B', '#0EA5E9', '#F59E0B', '#EF4444', '#22C55E', '#F97316', '#14B8A6'];

/* ── Create Revision Wizard ── */
function CreateRevisionWizard({ projectId, onClose, onCreated }) {
  const [form, setForm] = useState({
    revisionName: '', revisionDate: new Date().toISOString().slice(0, 10),
    reason: 'SCOPE_CHANGE', remarks: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.revisionName) { toast.error('Enter a revision name'); return; }
    setSaving(true);
    try {
      await api.post(`/v1/projects/${projectId}/cc/budget-revisions`, form);
      toast.success('Budget revision captured');
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create revision');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="w-full max-w-md rounded-2xl p-6 mx-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="create-revision-wizard">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Capture Budget Revision</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:opacity-70"><X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
          This will snapshot the current budget values of all cost codes as a new revision point.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Revision Name *</label>
            <Input value={form.revisionName} onChange={e => setForm(f => ({ ...f, revisionName: e.target.value }))}
              placeholder="e.g. Rev 4 - Q2 Re-baseline" className="text-sm" data-testid="revision-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Date</label>
              <Input type="date" value={form.revisionDate} onChange={e => setForm(f => ({ ...f, revisionDate: e.target.value }))}
                className="text-sm" data-testid="revision-date-input" />
            </div>
            <div>
              <label className="text-[10px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Reason</label>
              <select value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                className="w-full rounded-lg px-3 py-2 text-sm h-9" data-testid="revision-reason-select"
                style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                <option value="INITIAL">Initial Baseline</option>
                <option value="SCOPE_CHANGE">Scope Change</option>
                <option value="COST_ESCALATION">Cost Escalation</option>
                <option value="MANAGEMENT_RESERVE">Management Reserve</option>
                <option value="RE_BASELINE">Re-baseline</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Remarks</label>
            <Input value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
              placeholder="Reason for this revision..." className="text-sm" data-testid="revision-remarks-input" />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-revision-btn"
            style={{ backgroundColor: '#3B82F6', color: '#fff' }}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Capture Snapshot
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function BudgetRevisionTracker() {
  const { projectId: ctxProjectId } = useProject();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(true);
  const [revisions, setRevisions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedRevs, setSelectedRevs] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [comparingLoading, setComparingLoading] = useState(false);

  useEffect(() => {
    api.get('/v1/cc/projects-with-data').then(r => {
      setProjects(r.data.projects || []);
      if (r.data.projects?.length > 0) setSelectedProject(r.data.projects[0].project_id);
    }).catch(() => toast.error('Failed to load projects'));
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const res = await api.get(`/v1/projects/${selectedProject}/cc/budget-revisions`);
      setRevisions(res.data.revisions || []);
      setComparison(null);
      setSelectedRevs([]);
      setCompareMode(false);
    } catch {
      toast.error('Failed to load revisions');
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleToggleSelect = (revId) => {
    setSelectedRevs(prev => {
      if (prev.includes(revId)) return prev.filter(r => r !== revId);
      if (prev.length >= 2) return [prev[1], revId];
      return [...prev, revId];
    });
  };

  const handleCompare = async () => {
    if (selectedRevs.length !== 2) { toast.error('Select exactly 2 revisions'); return; }
    setComparingLoading(true);
    try {
      const res = await api.get(`/v1/projects/${selectedProject}/cc/budget-revisions/compare/${selectedRevs[0]}/${selectedRevs[1]}`);
      setComparison(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Comparison failed');
    } finally {
      setComparingLoading(false);
    }
  };

  const handleDelete = async (revId) => {
    try {
      await api.delete(`/v1/projects/${selectedProject}/cc/budget-revisions/${revId}`);
      toast.success('Revision deleted');
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  /* Evolution chart data */
  const evolutionData = useMemo(() =>
    revisions.map(r => ({
      name: r.revisionName.length > 20 ? r.revisionName.slice(0, 18) + '...' : r.revisionName,
      fullName: r.revisionName,
      date: r.revisionDate,
      budget: r.summary.totalCurrentBudget,
      committed: r.summary.totalCommitted,
      incurred: r.summary.totalIncurred,
    }))
  , [revisions]);

  /* Category evolution across revisions */
  const categoryEvolution = useMemo(() => {
    if (revisions.length < 2) return [];
    const allCats = new Set();
    revisions.forEach(r => Object.keys(r.summary.categoryBreakdown || {}).forEach(c => allCats.add(c)));
    return Array.from(allCats).map(cat => {
      const row = { category: cat.replace(/_/g, ' ') };
      revisions.forEach((r, i) => {
        row[`rev${i}`] = r.summary.categoryBreakdown?.[cat]?.current || 0;
      });
      return row;
    });
  }, [revisions]);

  /* Comparison grid columns */
  const compColDefs = useMemo(() => {
    if (!comparison) return [];
    return [
      { field: 'costCode', headerName: 'Code', width: 80, pinned: 'left', cellStyle: { fontWeight: 600 } },
      { field: 'costCodeName', headerName: 'Description', flex: 1, minWidth: 180 },
      { field: 'costCategory', headerName: 'Category', width: 130 },
      { field: 'rev1Budget', headerName: comparison.revision1.revisionName.slice(0, 18), width: 120, type: 'numericColumn', valueFormatter: p => fmt(p.value) },
      { field: 'rev2Budget', headerName: comparison.revision2.revisionName.slice(0, 18), width: 120, type: 'numericColumn', valueFormatter: p => fmt(p.value) },
      { field: 'delta', headerName: 'Delta', width: 110, type: 'numericColumn', valueFormatter: p => fmt(p.value),
        cellStyle: p => ({ color: p.value > 0 ? '#EF4444' : p.value < 0 ? '#22C55E' : 'var(--text-muted)', fontWeight: 600 }) },
      { field: 'deltaPct', headerName: '%', width: 80, type: 'numericColumn', valueFormatter: p => `${p.value}%`,
        cellStyle: p => ({ color: p.value > 0 ? '#EF4444' : p.value < 0 ? '#22C55E' : 'var(--text-muted)' }) },
      { field: 'rev1Committed', headerName: 'Comm. (R1)', width: 110, type: 'numericColumn', valueFormatter: p => fmt(p.value) },
      { field: 'rev2Committed', headerName: 'Comm. (R2)', width: 110, type: 'numericColumn', valueFormatter: p => fmt(p.value) },
    ];
  }, [comparison]);

  const defaultColDef = useMemo(() => ({
    sortable: true, filter: true, resizable: true,
    cellStyle: { fontSize: '12px', display: 'flex', alignItems: 'center' },
  }), []);

  return (
    <DashboardLayout>
      {ctxProjectId && <AIAnalystPanel projectId={ctxProjectId} moduleKey="BUDGET_REVISION" defaultOpen={true} compact={true} />}

      <div className="space-y-5" data-testid="budget-revision-page">
        {/* Header */}
        <div className="flex items-center gap-4 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate('/project-controls/cost')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="p-2.5 rounded-xl" style={{ background: '#3B82F612', border: '1px solid #3B82F620' }}>
            <FileText className="w-5 h-5" style={{ color: '#3B82F6' }} />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Budget Revision Tracker</h1>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Capture, compare, and track budget evolution</p>
          </div>
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
            className="rounded-lg px-3 py-2 text-sm" data-testid="project-selector"
            style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
            {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
          </select>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="capture-revision-btn"
            style={{ backgroundColor: '#3B82F6', color: '#fff' }}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Capture Revision
          </Button>
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-text)' }} /></div>
        ) : revisions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <FileText className="w-12 h-12 mb-4" style={{ color: 'var(--text-disabled)' }} />
            <p className="text-sm mb-2" style={{ color: 'var(--text-muted)' }}>No budget revisions captured yet</p>
            <Button size="sm" onClick={() => setShowCreate(true)} style={{ backgroundColor: '#3B82F6', color: '#fff' }}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Capture First Revision
            </Button>
          </div>
        ) : (
          <>
            {/* Budget Evolution Chart */}
            <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="evolution-chart">
              <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Budget Evolution</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={evolutionData} margin={{ top: 10, right: 20, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickFormatter={v => fmt(v)} />
                  <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }}
                    formatter={(v, name) => [fmt(v), name]} labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="budget" name="Current Budget" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="committed" name="Committed" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="incurred" name="Incurred" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Revision Cards + Compare */}
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Revisions ({revisions.length})
              </h3>
              <div className="flex items-center gap-2">
                {compareMode && (
                  <>
                    <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                      Selected: {selectedRevs.length}/2
                    </span>
                    <Button size="sm" onClick={handleCompare} disabled={selectedRevs.length !== 2 || comparingLoading}
                      data-testid="compare-btn" style={{ backgroundColor: '#143A6B', color: '#fff' }}>
                      {comparingLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <GitCompare className="w-3.5 h-3.5 mr-1" />}
                      Compare
                    </Button>
                  </>
                )}
                <Button variant={compareMode ? 'default' : 'outline'} size="sm"
                  onClick={() => { setCompareMode(!compareMode); setSelectedRevs([]); setComparison(null); }}
                  data-testid="toggle-compare-btn"
                  style={compareMode ? { backgroundColor: '#143A6B', color: '#fff' } : {}}>
                  <GitCompare className="w-3.5 h-3.5 mr-1" /> {compareMode ? 'Exit Compare' : 'Compare'}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="revision-cards">
              {revisions.map((rev, idx) => {
                const isSelected = selectedRevs.includes(rev.revisionId);
                const prevBudget = idx > 0 ? revisions[idx - 1].summary.totalCurrentBudget : null;
                const delta = prevBudget !== null ? rev.summary.totalCurrentBudget - prevBudget : null;
                const deltaPct = prevBudget && prevBudget > 0 ? (delta / prevBudget * 100) : null;
                const rc = REASON_COLORS[rev.reason] || '#94A3B8';
                return (
                  <div key={rev.revisionId}
                    className={`rounded-xl p-4 transition-all ${compareMode ? 'cursor-pointer' : ''}`}
                    onClick={() => compareMode && handleToggleSelect(rev.revisionId)}
                    data-testid={`revision-card-${rev.revisionId}`}
                    style={{
                      backgroundColor: 'var(--card-bg)',
                      border: isSelected ? `2px solid #143A6B` : '1px solid var(--border-color)',
                      transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                      boxShadow: isSelected ? '0 4px 12px rgba(139,92,246,0.2)' : 'none'
                    }}>
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                        style={{ backgroundColor: `${rc}15`, color: rc, border: `1px solid ${rc}30` }}>
                        {rev.reason.replace(/_/g, ' ')}
                      </span>
                      {!compareMode && (
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(rev.revisionId); }}
                          className="p-1 rounded hover:opacity-70" data-testid={`delete-rev-${rev.revisionId}`}>
                          <Trash2 className="w-3 h-3" style={{ color: 'var(--text-disabled)' }} />
                        </button>
                      )}
                      {compareMode && (
                        <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center"
                          style={{ borderColor: isSelected ? '#143A6B' : 'var(--border-color)', backgroundColor: isSelected ? '#143A6B' : 'transparent' }}>
                          {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                      )}
                    </div>
                    <h4 className="text-sm font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>{rev.revisionName}</h4>
                    <div className="flex items-center gap-1.5 text-[10px] mb-3" style={{ color: 'var(--text-disabled)' }}>
                      <CalendarDays className="w-3 h-3" /> {rev.revisionDate}
                    </div>
                    <div className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{fmt(rev.summary.totalCurrentBudget)}</div>
                    {delta !== null && (
                      <div className="flex items-center gap-1 text-[11px]">
                        {delta > 0 ? <ArrowUpRight className="w-3 h-3" style={{ color: '#EF4444' }} /> : <ArrowDownRight className="w-3 h-3" style={{ color: '#22C55E' }} />}
                        <span style={{ color: delta > 0 ? '#EF4444' : '#22C55E', fontWeight: 600 }}>
                          {delta > 0 ? '+' : ''}{fmt(delta)} ({deltaPct > 0 ? '+' : ''}{deltaPct?.toFixed(1)}%)
                        </span>
                        <span style={{ color: 'var(--text-disabled)' }}>vs prev</span>
                      </div>
                    )}
                    {rev.remarks && <p className="text-[10px] mt-2 line-clamp-2" style={{ color: 'var(--text-muted)' }}>{rev.remarks}</p>}
                    <div className="grid grid-cols-2 gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
                      <div className="text-[10px]">
                        <span style={{ color: 'var(--text-disabled)' }}>Committed</span>
                        <div className="font-medium" style={{ color: '#F59E0B' }}>{fmt(rev.summary.totalCommitted)}</div>
                      </div>
                      <div className="text-[10px]">
                        <span style={{ color: 'var(--text-disabled)' }}>Incurred</span>
                        <div className="font-medium" style={{ color: '#EF4444' }}>{fmt(rev.summary.totalIncurred)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Comparison Results */}
            {comparison && (
              <div className="space-y-5" data-testid="comparison-results">
                {/* Comparison Header */}
                <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                    Comparison: {comparison.revision1.revisionName} vs {comparison.revision2.revisionName}
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)' }}>
                      <div className="text-[10px] mb-1" style={{ color: 'var(--text-disabled)' }}>{comparison.revision1.revisionName}</div>
                      <div className="text-lg font-bold" style={{ color: '#3B82F6' }}>{fmt(comparison.revision1.totalBudget)}</div>
                      <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{comparison.revision1.revisionDate}</div>
                    </div>
                    <div className="flex flex-col items-center justify-center">
                      <ChevronRight className="w-5 h-5 mb-1" style={{ color: 'var(--text-disabled)' }} />
                      <div className="text-lg font-bold" style={{ color: comparison.totalDelta > 0 ? '#EF4444' : '#22C55E' }}>
                        {comparison.totalDelta > 0 ? '+' : ''}{fmt(comparison.totalDelta)}
                      </div>
                      <div className="text-[10px]" style={{ color: comparison.totalDelta > 0 ? '#EF4444' : '#22C55E' }}>
                        {comparison.totalDeltaPct > 0 ? '+' : ''}{comparison.totalDeltaPct}%
                      </div>
                    </div>
                    <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)' }}>
                      <div className="text-[10px] mb-1" style={{ color: 'var(--text-disabled)' }}>{comparison.revision2.revisionName}</div>
                      <div className="text-lg font-bold" style={{ color: '#143A6B' }}>{fmt(comparison.revision2.totalBudget)}</div>
                      <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{comparison.revision2.revisionDate}</div>
                    </div>
                  </div>
                </div>

                {/* Category Delta Chart */}
                <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="category-delta-chart">
                  <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Category Budget Change</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={comparison.categories} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                      <XAxis type="number" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickFormatter={v => fmt(v)} />
                      <YAxis dataKey="category" type="category" width={120} tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
                        tickFormatter={v => v.replace(/_/g, ' ')} />
                      <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }}
                        formatter={(v) => fmt(v)} />
                      <ReferenceLine x={0} stroke="var(--text-muted)" strokeDasharray="3 3" />
                      <Bar dataKey="delta" name="Budget Delta" radius={[0, 4, 4, 0]}>
                        {(comparison.categories || []).map((entry, idx) => (
                          <Cell key={idx} fill={entry.delta > 0 ? '#EF4444' : '#22C55E'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Comparison Grid */}
                <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="comparison-grid">
                  <div className="p-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      Cost Code Comparison ({comparison.costCodes.length} items)
                    </h3>
                  </div>
                  <div className="ag-theme-alpine" style={{ height: 400, width: '100%', '--ag-background-color': 'var(--card-bg)', '--ag-header-background-color': 'var(--table-header-bg)', '--ag-odd-row-background-color': 'var(--table-row-alt)', '--ag-row-hover-color': 'var(--table-row-hover)', '--ag-header-foreground-color': 'var(--text-primary)', '--ag-foreground-color': 'var(--text-primary)', '--ag-border-color': 'var(--border-color)', '--ag-font-size': '12px', '--ag-row-height': '38px', '--ag-header-height': '36px' }}>
                    <AgGridReact rowData={comparison.costCodes} columnDefs={compColDefs} defaultColDef={defaultColDef}
                      animateRows suppressCellFocus pagination paginationPageSize={50}
                      getRowId={p => p.data.costCode} />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {showCreate && <CreateRevisionWizard projectId={selectedProject} onClose={() => setShowCreate(false)} onCreated={loadData} />}

        {selectedProject && (
          <ModuleInsightsPanel projectId={selectedProject} moduleKey="budget" color="#0EA5E9" deepLinkBase="/project-controls/cost/budget-revisions" />
        )}
      </div>
    </DashboardLayout>
  );
}
